import { ERegionCode } from '@sureapp/platform-sdk';
import { mergeConfigs } from '@sureapp/wl-toggle-common/dist/services/mergeConfig';
import { isServer } from '@sureapp/wl-toggle-common/dist/utils/env';
import clonedeep from 'lodash/cloneDeep';

import { generateWlConfig } from '@@/auto/constants/wlConfig';
import { TConfig, TConfigInput, TConfigParams } from '@@/auto/interfaces';
import { handleFunctionConfig } from '@@/auto/services/handleMergeConfigs';
import regionConfigMerge, {
  buildFunctionalConfig,
} from '@@/auto/services/regionConfigMerge';

import { regionCodes } from '../constants/configPatternRegionCodes';

type TOpts = {
  tenant: string;
  partner?: string;
  region?: ERegionCode;
  configParams: TConfigParams;
  shouldUseDeprecatedConfig?: boolean;
};

// Try fetching the region overrides first. Fall back to region/:tenant/index
// if no region override exists.
function retrieveConfig({
  tenant,
  region,
  partner,
  configParams,
  shouldUseDeprecatedConfig = false,
}: TOpts) {
  /* eslint-disable import/no-dynamic-require, global-require */
  try {
    if (partner) {
      return handleFunctionConfig(
        require(`../constants/whitelabel/${
          partner === 'credit_karma' ? 'credit-karma' : partner
        }`).default,
        configParams
      );
    }
    return handleFunctionConfig(
      require(`../constants/region/${tenant}/${region}`).default,
      configParams
    );
  } catch {
    if (shouldUseDeprecatedConfig) {
      try {
        return handleFunctionConfig(
          require(`../constants/region/${tenant}`).default,
          configParams
        );
      } catch {
        return {};
      }
    }
    return {};
  }
  /* eslint-enable import/no-dynamic-require, global-require */
}

const tenant = process.env.REACT_APP_TENANT || 'toggle';

function getEnvConfig(opts: TOpts): DeepPartial<TConfig> {
  if (isServer || !tenant) {
    return {};
  }

  const { configTestHelper, Cypress } = window;

  const config = retrieveConfig(opts);

  return (
    (Cypress &&
      configTestHelper &&
      (configTestHelper(config) as DeepPartial<TConfig>)) ||
    config ||
    {}
  );
}

export function mergeFunctionalConfigs({
  defaultConfig,
  partnerConfig,
  toggleRegionConfig,
  partnerRegionConfig,
}: {
  defaultConfig: TConfig;
  partnerConfig: DeepPartial<TConfigInput>;
  toggleRegionConfig: DeepPartial<TConfigInput>;
  partnerRegionConfig: DeepPartial<TConfigInput>;
}) {
  const builtPartnerConfig = buildFunctionalConfig({
    defaultConfig,
    functionalConfig: partnerConfig,
  });
  const builtToggleRegionConfig = buildFunctionalConfig({
    defaultConfig: builtPartnerConfig,
    functionalConfig: toggleRegionConfig,
  });

  const builtPartnerRegionConfig = buildFunctionalConfig({
    defaultConfig: builtToggleRegionConfig,
    functionalConfig: partnerRegionConfig,
  });

  return builtPartnerRegionConfig;
}

export function getRegionConfigByCode(
  regionCode?: ERegionCode,
  configParams: TConfigParams = { isFeatureEnabled: () => false }
): TConfig {
  if (regionCode && regionCodes.includes(regionCode)) {
    const baseConfig = generateWlConfig(configParams);
    const partnerConfig = getEnvConfig({
      partner: tenant,
      tenant,
      configParams,
    });
    const toggleRegionConfig = getEnvConfig({
      region: regionCode,
      tenant: 'toggle',
      configParams,
    });
    const partnerRegionConfig = getEnvConfig({
      region: regionCode,
      tenant,
      configParams,
      shouldUseDeprecatedConfig: false,
    });

    return mergeFunctionalConfigs({
      defaultConfig: clonedeep(baseConfig),
      partnerConfig: clonedeep(partnerConfig),
      toggleRegionConfig: clonedeep(toggleRegionConfig),
      partnerRegionConfig: clonedeep(partnerRegionConfig),
    });
  }

  const baseConfig = generateWlConfig(configParams);
  const partnerConfig = getEnvConfig({
    partner: tenant,
    tenant,
    configParams,
  });

  const mergedPartnerConfig = buildFunctionalConfig({
    defaultConfig: clonedeep(baseConfig),
    functionalConfig: clonedeep(partnerConfig),
  });

  const mergedRegionConfig = mergeConfigs<DeepPartial<TConfig>>(
    getEnvConfig({
      region: regionCode,
      tenant: 'toggle',
      configParams,
      shouldUseDeprecatedConfig: true,
    }),
    getEnvConfig({
      region: regionCode,
      tenant,
      configParams,
      shouldUseDeprecatedConfig: true,
    })
  ) as TConfig;

  const completedConfig = regionConfigMerge({
    defaultConfig: mergedPartnerConfig,
    newConfig: mergedRegionConfig,
    regionCode,
  });

  return completedConfig;
}
